import { useState, useEffect } from 'react';
import { useApiData } from '../../contexts/apiService';
import { useTicketsContext } from '../../contexts/pdfTicketsContext';
import WarningBox from '../../components/common/WarningBox'
import './approvals.css';
import { USER_ROLE_LEVEL } from '../../contexts/userAccess';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FIREBASE_STORAGE } from '../../firebase/firebase';
import { getDownloadURL, ref } from 'firebase/storage';
import LoaderWheel from '../../components/common/LoaderWheel';
import { useDataManager } from "../../contexts/dataManager";
import { PendingApprovalItem } from '../../contexts/tableDefinitions';
import SQLTable from '../../components/sql_table/tableMain';
import { RowActionConfig } from '../../components/sql_table/tableMain';
import { getColConfigs } from './tableConfig';

const PendingApprovalsTable = () => {
	const { userDetails, apiData, isLoading, handleDeleteRow, handleEditOrder, handleAddRow, handleGetTable } = useApiData();
	const [sortedOtherApprovals, setSortedOtherApprovals] = useState<PendingApprovalItem[]>([]);
	const [sortedPendingApprovals, setSortedPendingApprovals] = useState<PendingApprovalItem[]>([]);
	const { availableTickets } = useTicketsContext();
	const [showPermissionWarning, setShowPermissionWarning] = useState(false);
	const { requestTableData, hasTableData } = useDataManager();
	const [showResolvedDrawer, setShowResolvedDrawer] = useState(false);

	// Applies an accepted "resolve_warning" approval: upserts the row's ignored_warnings
	// list in warning_adjustment, reusing the existing generic add-row/edit-row endpoints
	// (INSERT if no adjustment row exists yet for this table+row, else append to the
	// existing JSON array via a plain column edit) rather than a bespoke endpoint.
	//
	// Fetches fresh via handleGetTable rather than the cached apiData/requestTableData
	// path: requestTableData only enqueues its fetch (it resolves before the fetch
	// actually completes), so awaiting it and then reading apiData immediately after
	// would almost always see stale/missing data and risk inserting a duplicate
	// warning_adjustment row for the same table+row (violating its unique key).
	const applyResolveWarning = async (approval: PendingApprovalItem) => {
		const fetched = await handleGetTable('warning_adjustment');
		if (fetched === null) {
			console.error('Failed to fetch warning_adjustment; aborting resolve to avoid a duplicate/conflicting insert');
			return;
		}
		const adjustments = fetched || [];
		const existing = adjustments.find((row: any) =>
			row.sql_table_name === approval.table_name && String(row.row_id) === String(approval.pending_row_id)
		);

		if (existing) {
			let ignored: string[] = [];
			try {
				ignored = typeof existing.ignored_warnings === 'string'
					? JSON.parse(existing.ignored_warnings)
					: existing.ignored_warnings || [];
			} catch {
				ignored = [];
			}

			if (!ignored.includes(approval.new_value)) {
				const updated = [...ignored, approval.new_value];
				await handleEditOrder('warning_adjustment', existing.id, 'ignored_warnings', typeof existing.ignored_warnings === 'string' ? existing.ignored_warnings : JSON.stringify(ignored), JSON.stringify(updated));
			}
		} else {
			await handleAddRow('warning_adjustment', {
				sql_table_name: approval.table_name,
				row_id: Number(approval.pending_row_id),
				ignored_warnings: JSON.stringify([approval.new_value]),
			});
		}
	};

	const handleAcceptApproval = async (approval: PendingApprovalItem, reason?: string) => {
		//to-do: add reason to audit log when approving
		if (
			userDetails && (userDetails.role === 'super_admin' ||
				(
					(userDetails.role === 'schedule_admin' || userDetails.role === 'admin' || userDetails.role === 'accounting_admin') &&
					(userDetails.name !== approval.request_user)
				))
		) {
			if (approval.action === 'delete') {
				handleDeleteRow(new Set([approval.pending_row_id]), approval.table_name)
			}
			else if (approval.action === 'edit') {
				await handleEditOrder(approval.table_name, approval.pending_row_id, approval.edited_col, approval.old_value, approval.new_value)
			}
			else if (approval.action === 'resolve_warning') {
				await applyResolveWarning(approval)
			}

			await handleEditOrder('pending_approvals', approval.id, 'status', 'Pending', 'Accepted', undefined, reason)

			if (approval.action === 'edit') {
				// setting pending status back to 0
				const columnMapping: Record<string, string> = {
					'actual_destination': 'pending_dest',
					'gross_barrels': 'pending_gross',
					'net_barrels': 'pending_net',
					'observed_gravity': 'pending_grav',
					'corrected_gravity': 'pending_corr_grav',
					'observed_temperature': 'pending_temp',
					'bsw': 'pending_bsw'
				};
				
				const mappedColumn = columnMapping[approval.edited_col];
				if (!mappedColumn) {
					return; // Exit early if no mapping exists
				}
				
				await handleEditOrder(approval.table_name, approval.pending_row_id, mappedColumn, '1', '0')
			}
		}
		else {
			setShowPermissionWarning(true);
		}
	};

	const handleRejectApproval = async (approval: PendingApprovalItem, reason?: string) => {
		//to-do: add reason to audit log when rejecting
		if (
			userDetails && (userDetails.role === 'super_admin' ||
				(
					(userDetails.role === 'schedule_admin' || userDetails.role === 'admin' || userDetails.role === 'accounting_admin') &&
					(userDetails.name !== approval.request_user)
				))
		) {
			try {
				// Update the approval status to Rejected
				await handleEditOrder('pending_approvals', approval.id, 'status', 'Pending', 'Rejected', undefined, reason)

				// setting pending status back to 0 (only for columns that have pending flags)
				const columnMapping: Record<string, string> = {
					'actual_destination': 'pending_dest',
					'gross_barrels': 'pending_gross',
					'net_barrels': 'pending_net',
					'observed_gravity': 'pending_grav',
					'corrected_gravity': 'pending_corr_grav',
					'observed_temperature': 'pending_temp',
					'bsw': 'pending_bsw'
				};

				// Only reset pending flag if the edited column has a corresponding pending flag
				if (columnMapping[approval.edited_col]) {
					const mappedColumn = columnMapping[approval.edited_col];
					await handleEditOrder(approval.table_name, approval.pending_row_id, mappedColumn, '1', '0', undefined)
				}
			} catch (error) {
				console.error('Error rejecting approval:', error);
			}
		}
		else {
			setShowPermissionWarning(true);
		}
	};

	const FORBIDDEN_ROLES_TO_APPROVE = ["viewer", "scheduler", "accountant"];
	const ROLES_NOT_ALLOWED_TO_APPROVE_OTHERS = {
		schedule_admin: ["accountant"],
		accounting_admin: ["schedule_admin", "scheduler"],
	};

	// Get column configurations from master config file
	const COL_CONFIGS = getColConfigs();


	useEffect(() => {
		async function enrichApprovals() {
			if (!apiData?.pending_approvals) return;
			const otherApprovals = apiData?.pending_approvals.filter(
				(a: PendingApprovalItem) => a.status !== 'Pending'
			);

			const sorted = [...otherApprovals].sort(
				(a, b) => new Date(b.reviewed_on).getTime() - new Date(a.reviewed_on).getTime()
			);

			// Debug: Check if approval_notes are present in the data
			console.log('Resolved approvals sample:', sorted.slice(0, 3));
			console.log('Approval notes check:', sorted.map(a => ({ id: a.id, approval_notes: a.approval_notes })).slice(0, 5));

			const pendingApprovals = apiData?.pending_approvals.filter(
				(approval: PendingApprovalItem) => approval.status === 'Pending'
			);
			const sortedPendingApprovals = [...pendingApprovals].sort((a, b) =>
				new Date(b.requested_on).getTime() - new Date(a.requested_on).getTime()
			);

			const sortedPendingApprovalsEnriched = sortedPendingApprovals
				.map(pendingApproval => {
					const loggedUserRoleLevel = userDetails ? USER_ROLE_LEVEL[userDetails.role as keyof typeof USER_ROLE_LEVEL] : USER_ROLE_LEVEL["viewer"];
					const pendingApprovalUserRoleLevel = USER_ROLE_LEVEL[pendingApproval.request_user_role as keyof typeof USER_ROLE_LEVEL] || USER_ROLE_LEVEL["viewer"];
					const isRoleAllowed = !(FORBIDDEN_ROLES_TO_APPROVE.includes(userDetails?.role || ''));
					let forbiddenToApproveOthersRoles = false;
					if (ROLES_NOT_ALLOWED_TO_APPROVE_OTHERS[userDetails?.role as keyof typeof ROLES_NOT_ALLOWED_TO_APPROVE_OTHERS]) {
						forbiddenToApproveOthersRoles = ROLES_NOT_ALLOWED_TO_APPROVE_OTHERS[userDetails?.role as keyof typeof ROLES_NOT_ALLOWED_TO_APPROVE_OTHERS]
							.includes(pendingApproval.request_user_role);
					}
					const isUserAllowedToApprove = isRoleAllowed && userDetails
						? loggedUserRoleLevel >= pendingApprovalUserRoleLevel &&
						!forbiddenToApproveOthersRoles &&
						userDetails.id !== pendingApproval.request_user_id
						: false

					// Warning adjustment approvals require minimum admin level
					const requiresAdmin = pendingApproval.action === 'resolve_warning';
					const meetsAdminRequirement = !requiresAdmin || loggedUserRoleLevel >= USER_ROLE_LEVEL['admin'];

					const isSuperAdmin = userDetails?.role === 'super_admin';
					return {
						...pendingApproval,
						isUserAllowedToApprove: isSuperAdmin || (isUserAllowedToApprove && meetsAdminRequirement),
					}
				});

			setSortedOtherApprovals(sorted);
			setSortedPendingApprovals(sortedPendingApprovalsEnriched);
		}

		enrichApprovals();
	}, [apiData, userDetails]);

	// Check if PDF is available for a row
	const isPdfAvailableForRow = (rowData: any) => {
		if (!rowData.pdf_ticket_number || !rowData.pdf_carrier) return false;
		const ticketIdentifier = `${rowData.pdf_ticket_number}_${rowData.pdf_carrier}`;
		return availableTickets.includes(ticketIdentifier);
	};

	// Handle PDF click for row action
	const handlePdfClickAction = async (rowData: any) => {
		const ticketIdentifier = `${rowData.pdf_ticket_number}_${rowData.pdf_carrier}_${rowData.pdf_producer}.pdf`;

		try {
			const pdfRef = ref(FIREBASE_STORAGE, `tickets/${rowData.pdf_producer}/${ticketIdentifier}`);
			const url = await getDownloadURL(pdfRef);
			window.open(url, '_blank');
		} catch (error) {
			console.error('Error opening PDF:', error);
			alert('Error opening PDF. Please try again later.');
		}
	};

	// Define row actions for pending approvals
	const pendingApprovalActions: RowActionConfig[] = [
		{
			id: 'approve',
			label: 'Accept',
			variant: 'success',
			onClick: (rowData, rowId, reason) => handleAcceptApproval(rowData, reason),
			isVisible: (rowData) => rowData.status === 'Pending',
			isDisabled: (rowData) => !rowData.isUserAllowedToApprove,
			requiresConfirmation: true,
			confirmationConfig: {
				title: 'Approve Change',
				message: (rowData) => {
					const userRoleLevel = userDetails ? USER_ROLE_LEVEL[userDetails.role as keyof typeof USER_ROLE_LEVEL] : 1;
					const sensitiveTable = rowData.table_name === 'main_table' || rowData.table_name === 'schedule_table';

					// Require reason for lower permission levels or sensitive tables
					const requireReason = userRoleLevel <= 3 || sensitiveTable;

					if (requireReason) {
						return `You are about to approve a sensitive ${rowData.action} action on ${rowData.table_name}. Please provide a reason.`;
					}
					return `Approve ${rowData.action} action on ${rowData.table_name}?`;
				},
				requiresReason: (rowData: any) => {
					const userRoleLevel = userDetails ? USER_ROLE_LEVEL[userDetails.role as keyof typeof USER_ROLE_LEVEL] : 1;
					const sensitiveTable = rowData.table_name === 'main_table' || rowData.table_name === 'schedule_table';
					return userRoleLevel <= 3 || sensitiveTable;
				},
			},
		},
		{
			id: 'reject',
			label: 'Reject',
			variant: 'danger',
			onClick: (rowData, rowId, reason) => handleRejectApproval(rowData, reason),
			isVisible: (rowData) => rowData.status === 'Pending',
			isDisabled: (rowData) => !rowData.isUserAllowedToApprove,
		},
		{
			id: 'view_pdf',
			label: 'PDF',
			icon: <FontAwesomeIcon icon={faFilePdf} />,
			variant: 'primary',
			onClick: (rowData) => handlePdfClickAction(rowData),
			isVisible: (rowData) => isPdfAvailableForRow(rowData),
		}
	];


	return (
		<div className="mainContainer">
			<div className="approvals-contentContainer">
				<div className="approvals-pending-section">
					<h2 className="approvals-section-title">Pending Approvals</h2>
					<div className="approvals-table-container">
						<SQLTable
							tableName="pending_approvals"
							tableData={sortedPendingApprovals}
							idColumn="id"
							columnConfigs={COL_CONFIGS['pending_approvals'].columns}
							showHeader={true}
							selectableRows={false}
							multipleSelection={false}
							deletableRows={false}
							enableFilters={true}
							enableWarnings={false}
							sortOrder={COL_CONFIGS['pending_approvals'].sortOrder}
							showTable={true}
							rowActions={pendingApprovalActions}
							rowActionsPosition="end"
							rowActionsWidth={170}
						/>
					</div>
				</div>

				<button
					className={`approvals-toggle-button ${showResolvedDrawer ? 'attached' : 'floating'}`}
					onClick={() => setShowResolvedDrawer(!showResolvedDrawer)}
				>
					{showResolvedDrawer ? 'Hide' : 'Show'} Resolved Approvals
				</button>

				<div className="approvals-resizeHandle">
					<div className="approvals-resizeHandle-bar" />
				</div>

				<div className={`approvals-resolved-drawer ${showResolvedDrawer ? 'open' : 'closed'}`}>
					<SQLTable
						tableName="pending_approvals"
						tableData={sortedOtherApprovals}
						idColumn="id"
						columnConfigs={COL_CONFIGS['pending_approvals_resolved'].columns}
						showHeader={true}
						selectableRows={false}
						multipleSelection={false}
						deletableRows={false}
						enableFilters={true}
						enableWarnings={false}
						sortOrder={COL_CONFIGS['pending_approvals_resolved'].sortOrder}
						showTable={true}
					/>
				</div>
			</div>

			<WarningBox
				isOpen={showPermissionWarning}
				onClose={() => {
					setShowPermissionWarning(false);
				}}
				title="Missing Permissions"
				showbutton2={false}
				message={`The requested action requires extra permissions.`}
				button1="Cancel"
			/>
		</div>
	);
};

export default PendingApprovalsTable;